import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, shareReplay, catchError, finalize } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Organization } from '../../../services/organization.service';
import { Ability, Permissions, PermissionStatus } from '../../../services/permissions.service';

/**
 * Admin feature keys. MUST stay in sync with the Go AdminFeature* constants in
 * locations-api/api/handlers/admin.go (users=1, sharing=2, jobs=3, utilization=4).
 */
export type AdminFeature = 'users' | 'sharing' | 'jobs' | 'utilization';

export const ADMIN_FEATURES: { key: AdminFeature; label: string; description: string }[] = [
  { key: 'users', label: 'User Directory', description: 'View the Keycloak user directory' },
  { key: 'sharing', label: 'Org & Sharing', description: 'Manage orgs, members and object permissions' },
  { key: 'jobs', label: 'Jobs Ops', description: 'View and cancel running jobs' },
  { key: 'utilization', label: 'Utilization', description: 'View org and user product utilization' },
];

export interface AdminCapabilities {
  is_super_admin: boolean;
  features: AdminFeature[];
}

export interface KeycloakUser {
  id: string;
  username: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  emailVerified?: boolean;
  enabled?: boolean;
}

export interface UserOrg {
  uuid: string;
  name: string | null;
  licensed_seats: number | null;
  ability: Ability;
}

export interface UserDetail extends KeycloakUser {
  createdTimestamp?: number;
  organizations: UserOrg[];
}

export interface AdminJob {
  id: string;
  workflow_run_id: string | null;
  pipeline_id: string | null;
  location_uuid: string;
  location_name: string | null;
  pipeline_name: string | null;
  pipeline_owner: string | null;
  job_type: string;
  status: string;
  progress: number | null;
  error_message: string | null;
  created_at: string | null;
  updated_at: string | null;
  completed_at: string | null;
}

export interface PipelineRecentJob {
  id: string;
  workflow_run_id: string | null;
  job_type: string;
  status: string;
  progress: number | null;
  error_message: string | null;
  created_at: string | null;
  completed_at: string | null;
}

export interface PipelineDetail {
  id: string;
  name: string;
  description: string | null;
  keycloak_user_id: string;
  saved_search_id: string | null;
  feed_id: number | null;
  metadata: unknown;
  auto_sync_enabled: boolean | null;
  sync_on_job_completion: boolean | null;
  sync_on_map_change: boolean | null;
  last_synced_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  search_name: string | null;
  search_query: string | null;
  search_filters: unknown;
  feed_uuid: string | null;
  feed_name: string | null;
  refresh_interval: string | null;
  feed_metadata: unknown;
  refreshed_at: string | null;
  entity_count: number;
  total_jobs: number;
  completed_jobs: number;
  failed_jobs: number;
  last_job_at: string | null;
  recent_jobs: PipelineRecentJob[];
}

export interface AdminEntry {
  keycloak_user_id: string;
  is_super_admin: boolean;
  features: AdminFeature[];
}

export interface InviteUserRequest {
  email: string;
  firstName: string;
  lastName: string;
  org_uuid: string;
  ability: Ability;
}

export interface InviteUserResponse {
  user_id: string;
  email: string;
  created: boolean; // false if the user already existed
  invited: boolean; // false if the action email failed to send
  warning?: string;
}

export type UtilizationRange = '7d' | '30d' | '90d';

export interface UtilizationSummary {
  range: UtilizationRange;
  since: string;
  active_orgs: number;
  active_users: number;
  searches: number;
  jobs: number;
  chat_messages: number;
  entities_discovered: number | null;
  usage_available: boolean;
}

export interface UtilizationOrgRow {
  org_uuid: string;
  org_name: string | null;
  member_count: number;
  active_users: number;
  searches: number;
  jobs: number;
  chat_messages: number;
  entities_discovered: number | null;
  source_scrapes: number | null;
  last_activity_at: string | null;
}

export interface UtilizationOrgsResponse {
  range: UtilizationRange;
  since: string;
  usage_available: boolean;
  orgs: UtilizationOrgRow[];
  note: string;
}

export interface UtilizationUserRow {
  keycloak_user_id: string;
  searches: number;
  jobs: number;
  chat_messages: number;
  entities_discovered: number | null;
  source_scrapes: number | null;
  last_activity_at: string | null;
}

export interface UtilizationOrgUsersResponse {
  range: UtilizationRange;
  since: string;
  org_uuid: string;
  org_name: string | null;
  usage_available: boolean;
  users: UtilizationUserRow[];
  note: string;
}

export interface UtilizationOpsVolumes {
  range: UtilizationRange;
  since: string;
  until: string;
  entities_created: number;
  sources_created: number;
  jobs_created: number;
  searches_created: number;
  chat_messages: number;
  sources_per_entity: number | null;
  entity_usage_units: number | null;
  scrape_usage_units: number | null;
  usage_available: boolean;
  sheet_tsv: string;
  note: string;
}

export interface AwsCostServiceRow {
  service: string;
  amount_usd: number;
  usage_quantity: number;
  category: string;
}

export interface AwsCostWindow {
  range: UtilizationRange;
  since: string;
  until: string;
  total_usd: number;
  services: AwsCostServiceRow[];
  last_imported_at: string | null;
  row_count: number;
  sheet_tsv: string;
  note: string;
}

export interface AwsCostRefreshResult {
  range: UtilizationRange;
  since: string;
  until: string;
  upserted: number;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class AdminService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.LocationsApiBaseUrl}/v1/admin`;
  private readonly orchestratorBase = environment.OrchestratorApiBaseUrl;

  // Capability state (drives route guards + sidebar).
  private readonly _capabilities = signal<AdminCapabilities | null>(null);
  readonly capabilities = this._capabilities.asReadonly();
  readonly isSuperAdmin = computed(() => this._capabilities()?.is_super_admin ?? false);
  readonly hasAnyCapability = computed(() => {
    const c = this._capabilities();
    return !!c && (c.is_super_admin || c.features.length > 0);
  });

  private capabilities$?: Observable<AdminCapabilities>;

  can(feature: AdminFeature): boolean {
    const c = this._capabilities();
    return !!c && (c.is_super_admin || c.features.includes(feature));
  }

  // Loads capabilities once and caches them; the in-flight request is shared.
  ensureCapabilities(): Observable<AdminCapabilities> {
    if (this._capabilities()) {
      return of(this._capabilities()!);
    }
    if (!this.capabilities$) {
      this.capabilities$ = this.http.get<AdminCapabilities>(`${this.base}/me/capabilities`).pipe(
        tap((c) => this._capabilities.set(c)),
        catchError(() => {
          const empty: AdminCapabilities = { is_super_admin: false, features: [] };
          this._capabilities.set(empty);
          return of(empty);
        }),
        finalize(() => (this.capabilities$ = undefined)),
        shareReplay(1),
      );
    }
    return this.capabilities$;
  }

  // Users
  listUsers(search?: string): Observable<KeycloakUser[]> {
    let params = new HttpParams();
    if (search?.trim()) {
      params = params.set('search', search.trim());
    }
    return this.http.get<KeycloakUser[]>(`${this.base}/users`, { params });
  }

  /**
   * Invite (create) a Keycloak user, assign them to an org, and trigger the
   * set-password + verify-email action email (sent by Keycloak over SES SMTP).
   */
  inviteUser(req: InviteUserRequest): Observable<InviteUserResponse> {
    return this.http.post<InviteUserResponse>(`${this.base}/users/invite`, req);
  }

  /** Full detail for one user (incl. enabled flag + org memberships). */
  getUserDetail(userId: string): Observable<UserDetail> {
    return this.http.get<UserDetail>(`${this.base}/users/${userId}`);
  }

  /** Patch a user's profile fields in Keycloak (only supplied fields change). */
  updateUser(userId: string, updates: { firstName?: string; lastName?: string; email?: string }): Observable<{ status: boolean }> {
    return this.http.patch<{ status: boolean }>(`${this.base}/users/${userId}`, updates);
  }

  /** Activate/deactivate a user (Keycloak `enabled`). false blocks login. */
  setUserEnabled(userId: string, enabled: boolean): Observable<{ status: boolean; enabled: boolean }> {
    return this.http.put<{ status: boolean; enabled: boolean }>(`${this.base}/users/${userId}/enabled`, { enabled });
  }

  /** Email the user a one-time set-password link (Keycloak over SES SMTP). */
  sendUserPasswordReset(userId: string): Observable<{ status: boolean }> {
    return this.http.put<{ status: boolean }>(`${this.base}/users/${userId}/reset-password`, {});
  }

  /** Everything about a pipeline for the admin drawer (search, feed, stats, recent jobs). */
  getPipelineDetail(pipelineId: string): Observable<PipelineDetail> {
    return this.http.get<PipelineDetail>(`${this.base}/pipelines/${pipelineId}`);
  }

  // Organizations & sharing
  listOrganizations(): Observable<Organization[]> {
    return this.http.get<Organization[]>(`${this.base}/organizations`);
  }

  createOrganization(name: string, licensedSeats: number): Observable<Organization> {
    return this.http.post<Organization>(`${this.base}/organizations`, {
      name,
      licensed_seats: licensedSeats,
    });
  }

  listOrganizationMembers(orgUuid: string): Observable<Permissions[]> {
    return this.http.get<Permissions[]>(`${this.base}/organizations/${orgUuid}/members`);
  }

  addOrganizationMember(orgUuid: string, userId: string, ability: Ability): Observable<PermissionStatus> {
    return this.http.post<PermissionStatus>(`${this.base}/organizations/${orgUuid}/members`, {
      keycloak_user_id: userId,
      ability,
    });
  }

  removeOrganizationMember(orgUuid: string, userId: string): Observable<PermissionStatus> {
    return this.http.delete<PermissionStatus>(`${this.base}/organizations/${orgUuid}/members/${userId}`);
  }

  /** Admin grant on any object (bypasses the org-membership/self guards). */
  grantPermission(objectType: string, objectId: string | number, userId: string, ability: Ability): Observable<PermissionStatus> {
    return this.http.put<PermissionStatus>(`${this.base}/permissions/${objectType}/${objectId}/${userId}`, { ability });
  }

  revokePermission(objectType: string, objectId: string | number, userId: string): Observable<PermissionStatus> {
    return this.http.delete<PermissionStatus>(`${this.base}/permissions/${objectType}/${objectId}/${userId}`);
  }

  // Jobs ops
  listJobs(statuses: string[] = ['running', 'processing', 'pending']): Observable<AdminJob[]> {
    let params = new HttpParams();
    for (const s of statuses) {
      params = params.append('status', s);
    }
    return this.http.get<AdminJob[]>(`${this.base}/jobs`, { params });
  }

  /** Cancel a workflow run via the orchestrator (it owns the Hatchet client). */
  cancelJob(workflowRunId: string): Observable<{ status: string; message: string }> {
    return this.http.post<{ status: string; message: string }>(
      `${this.orchestratorBase}/workflows/${workflowRunId}/cancel`,
      {},
    );
  }

  // Utilization (org / user product activity)
  getUtilizationSummary(range: UtilizationRange = '30d'): Observable<UtilizationSummary> {
    return this.http.get<UtilizationSummary>(`${this.base}/utilization/summary`, {
      params: new HttpParams().set('range', range),
    });
  }

  listUtilizationOrgs(range: UtilizationRange = '30d'): Observable<UtilizationOrgsResponse> {
    return this.http.get<UtilizationOrgsResponse>(`${this.base}/utilization/orgs`, {
      params: new HttpParams().set('range', range),
    });
  }

  listUtilizationOrgUsers(
    orgUuid: string,
    range: UtilizationRange = '30d',
  ): Observable<UtilizationOrgUsersResponse> {
    return this.http.get<UtilizationOrgUsersResponse>(
      `${this.base}/utilization/orgs/${orgUuid}/users`,
      { params: new HttpParams().set('range', range) },
    );
  }

  getUtilizationOpsVolumes(range: UtilizationRange = '30d'): Observable<UtilizationOpsVolumes> {
    return this.http.get<UtilizationOpsVolumes>(`${this.base}/utilization/volumes`, {
      params: new HttpParams().set('range', range),
    });
  }

  getAwsCosts(range: UtilizationRange = '30d'): Observable<AwsCostWindow> {
    return this.http.get<AwsCostWindow>(`${this.base}/utilization/aws-costs`, {
      params: new HttpParams().set('range', range),
    });
  }

  refreshAwsCosts(range: UtilizationRange = '30d'): Observable<AwsCostRefreshResult> {
    return this.http.post<AwsCostRefreshResult>(`${this.base}/utilization/aws-costs/refresh`, null, {
      params: new HttpParams().set('range', range),
    });
  }

  // Admin access matrix (super admin only)
  listAdmins(): Observable<AdminEntry[]> {
    return this.http.get<AdminEntry[]>(`${this.base}/admins`);
  }

  /** feature is an AdminFeature key or the literal 'super_admin'. */
  grantAdmin(userId: string, feature: AdminFeature | 'super_admin'): Observable<{ status: boolean }> {
    return this.http.put<{ status: boolean }>(`${this.base}/admins/${userId}/${feature}`, {});
  }

  revokeAdmin(userId: string, feature: AdminFeature | 'super_admin'): Observable<{ status: boolean }> {
    return this.http.delete<{ status: boolean }>(`${this.base}/admins/${userId}/${feature}`);
  }
}
